import { useState, useMemo, useRef, type FormEvent, type CSSProperties } from 'react'
import { marked } from 'marked'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import RichTextEditor from '@/components/common/RichTextEditor'
import type { Servicio, Moneda, CategoriaServicio, Seccion, ServicioRecurso, TipoRecurso } from '@/lib/types'
import { formatCurrency, sanitizeHtml } from '@/lib/utils'
import { IconEdit, IconTrash, IconPlus, IconX, IconCheckCircle, IconXCircle, IconFileText } from '@/components/icons/Icons'
import { PageHeader } from '@/components/ui'

/* ─── Constants ─── */

// Categoría se mantiene por compatibilidad con Cotizaciones (ya no organiza esta UI).
const CATEGORIAS: CategoriaServicio[] = [
  'Consultoría e Implementación',
  'Desarrollo a Medida',
  'Automatización e IA',
  'Soporte y Acompañamiento',
  'Infraestructura y Hosting',
  'Programa de Partners',
]

const TIPOS_RECURSO: TipoRecurso[] = ['presentacion', 'documento', 'pdf', 'plantilla', 'enlace', 'imagen', 'video']
/* Siete tipos que se distinguen pero no se ordenan: la escala categorica, no
   el semaforo. Un PDF no esta "mal" por ser rojo. */
const TIPO_META: Record<TipoRecurso, { label: string; color: string; bg: string }> = {
  presentacion: { label: 'Presentación', color: 'var(--cat-7)', bg: 'color-mix(in srgb, var(--cat-7) 12%, transparent)' },
  documento: { label: 'Documento', color: 'var(--cat-4)', bg: 'color-mix(in srgb, var(--cat-4) 12%, transparent)' },
  pdf: { label: 'PDF', color: 'var(--cat-6)', bg: 'color-mix(in srgb, var(--cat-6) 12%, transparent)' },
  plantilla: { label: 'Plantilla', color: 'var(--cat-2)', bg: 'color-mix(in srgb, var(--cat-2) 12%, transparent)' },
  enlace: { label: 'Enlace', color: 'var(--cat-1)', bg: 'color-mix(in srgb, var(--cat-1) 12%, transparent)' },
  imagen: { label: 'Imagen', color: 'var(--cat-3)', bg: 'color-mix(in srgb, var(--cat-3) 12%, transparent)' },
  video: { label: 'Video', color: 'var(--cat-5)', bg: 'color-mix(in srgb, var(--cat-5) 12%, transparent)' },
}

/* Markdown → HTML sanitizado (mismo pipeline que WikiArticleViewer) */
function renderMarkdown(md: string): string {
  const raw = marked.parse(md || '', { gfm: true, breaks: true }) as string
  return sanitizeHtml(raw)
}

const empty: Omit<Servicio, 'id' | 'recursos'> = {
  nombre: '', descripcion: '', precio_base: 0, moneda: 'USD',
  etiquetas: [], secciones: [], categoria: 'Consultoría e Implementación',
  incluye: [], no_incluye: [], industrias: [], documento: '',
}

/* Pequeños íconos inline */
const Ico = {
  search: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  ext: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>,
  link: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
  check: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  upload: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
  layers: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>,
}

/* ─── Component ─── */

export default function Servicios() {
  const { role } = useAuth()
  const api = useApi()
  const isAdmin = role === 'admin'
  const { data: items = [], isLoading: loading } = useCachedQuery<Servicio>('servicios')
  const { addMut, updateMut, removeMut, invalidate } = useCachedMutations('servicios')

  /* Vista */
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [fEtiquetas, setFEtiquetas] = useState<string[]>([])
  const [fIndustrias, setFIndustrias] = useState<string[]>([])
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  /* Modal servicio */
  const [modal, setModal] = useState(false)
  const [del, setDel] = useState<Servicio | null>(null)
  const [edit, setEdit] = useState<Servicio | null>(null)
  const [form, setForm] = useState(empty)
  const [saving, setSaving] = useState(false)
  const [tagIn, setTagIn] = useState('')
  const [indIn, setIndIn] = useState('')
  const [incluyeIn, setIncluyeIn] = useState('')
  const [noIncluyeIn, setNoIncluyeIn] = useState('')
  const [activeTab, setActiveTab] = useState<'general' | 'documento' | 'scope' | 'secciones'>('general')
  const [docPreview, setDocPreview] = useState(false)

  /* Modal sección (propuestas) */
  const [showSecModal, setShowSecModal] = useState(false)
  const [editSecIdx, setEditSecIdx] = useState<number | null>(null)
  const [secTitulo, setSecTitulo] = useState('')
  const [secContenido, setSecContenido] = useState('')

  /* Modal recurso */
  const [recModal, setRecModal] = useState(false)
  const [recEdit, setRecEdit] = useState<ServicioRecurso | null>(null)
  const [recMode, setRecMode] = useState<'enlace' | 'archivo'>('enlace')
  const [recForm, setRecForm] = useState({ tipo: 'enlace' as TipoRecurso, titulo: '', descripcion: '', url: '', publico: true, alcance: 'servicio' as 'servicio' | 'linea' })
  const [recSaving, setRecSaving] = useState(false)
  const [recDel, setRecDel] = useState<ServicioRecurso | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  /* ─── Derived ─── */
  const selected = selectedId != null ? items.find(s => s.id === selectedId) ?? null : null

  const allEtiquetas = useMemo(() => {
    const set = new Set<string>()
    items.forEach(s => (Array.isArray(s.etiquetas) ? s.etiquetas : []).forEach(t => set.add(t)))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [items])

  const allIndustrias = useMemo(() => {
    const set = new Set<string>()
    items.forEach(s => (Array.isArray(s.industrias) ? s.industrias : []).forEach(t => set.add(t)))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [items])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(s => {
      if (q && !(s.nombre.toLowerCase().includes(q) || (s.descripcion || '').toLowerCase().includes(q))) return false
      const et = Array.isArray(s.etiquetas) ? s.etiquetas : []
      const ind = Array.isArray(s.industrias) ? s.industrias : []
      if (fEtiquetas.length && !fEtiquetas.every(t => et.includes(t))) return false
      if (fIndustrias.length && !fIndustrias.every(t => ind.includes(t))) return false
      return true
    })
  }, [items, search, fEtiquetas, fIndustrias])

  /* ─── Form helpers ─── */
  const resetForm = () => { setTagIn(''); setIndIn(''); setIncluyeIn(''); setNoIncluyeIn(''); setShowSecModal(false); setEditSecIdx(null); setSecTitulo(''); setSecContenido(''); setActiveTab('general'); setDocPreview(false) }
  const openNew = () => { setEdit(null); setForm({ ...empty }); resetForm(); setModal(true) }
  const openEdit = (s: Servicio) => {
    setEdit(s)
    setForm({
      nombre: s.nombre, descripcion: s.descripcion ?? '', precio_base: s.precio_base,
      moneda: s.moneda, etiquetas: Array.isArray(s.etiquetas) ? s.etiquetas : [],
      secciones: Array.isArray(s.secciones) ? s.secciones : [],
      categoria: s.categoria || 'Consultoría e Implementación',
      incluye: Array.isArray(s.incluye) ? s.incluye : [],
      no_incluye: Array.isArray(s.no_incluye) ? s.no_incluye : [],
      industrias: Array.isArray(s.industrias) ? s.industrias : [],
      documento: s.documento ?? '',
    })
    resetForm(); setModal(true)
  }

  const addTag = () => { const t = tagIn.trim(); if (t && !form.etiquetas.includes(t)) { setForm(f => ({ ...f, etiquetas: [...f.etiquetas, t] })); setTagIn('') } }
  const rmTag = (t: string) => setForm(f => ({ ...f, etiquetas: f.etiquetas.filter(x => x !== t) }))
  const addInd = () => { const t = indIn.trim(); if (t && !form.industrias.includes(t)) { setForm(f => ({ ...f, industrias: [...f.industrias, t] })); setIndIn('') } }
  const rmInd = (t: string) => setForm(f => ({ ...f, industrias: f.industrias.filter(x => x !== t) }))
  const addIncluye = () => { const t = incluyeIn.trim(); if (t && !form.incluye.includes(t)) { setForm(f => ({ ...f, incluye: [...f.incluye, t] })); setIncluyeIn('') } }
  const rmIncluye = (t: string) => setForm(f => ({ ...f, incluye: f.incluye.filter(x => x !== t) }))
  const addNoIncluye = () => { const t = noIncluyeIn.trim(); if (t && !form.no_incluye.includes(t)) { setForm(f => ({ ...f, no_incluye: [...f.no_incluye, t] })); setNoIncluyeIn('') } }
  const rmNoIncluye = (t: string) => setForm(f => ({ ...f, no_incluye: f.no_incluye.filter(x => x !== t) }))

  /* Secciones */
  const openNewSeccion = () => { setEditSecIdx(null); setSecTitulo(''); setSecContenido(''); setShowSecModal(true) }
  const openEditSeccion = (idx: number) => { setEditSecIdx(idx); setSecTitulo(form.secciones[idx].titulo); setSecContenido(form.secciones[idx].contenido); setShowSecModal(true) }
  const saveSeccion = () => {
    if (!secTitulo.trim()) { showToast('Título requerido', 'error'); return }
    const s: Seccion = { titulo: secTitulo.trim(), contenido: secContenido }
    if (editSecIdx !== null) { setForm(f => ({ ...f, secciones: f.secciones.map((x, i) => i === editSecIdx ? s : x) })) }
    else { setForm(f => ({ ...f, secciones: [...f.secciones, s] })) }
    setShowSecModal(false); setEditSecIdx(null); setSecTitulo(''); setSecContenido('')
  }
  const closeSecModal = () => { setShowSecModal(false); setEditSecIdx(null); setSecTitulo(''); setSecContenido('') }
  const removeSeccion = (idx: number) => setForm(f => ({ ...f, secciones: f.secciones.filter((_, i) => i !== idx) }))
  const moveSeccion = (idx: number, dir: -1 | 1) => {
    const target = idx + dir
    if (target < 0 || target >= form.secciones.length) return
    setForm(f => { const arr = [...f.secciones]; [arr[idx], arr[target]] = [arr[target], arr[idx]]; return { ...f, secciones: arr } })
  }

  /* Submit servicio */
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.nombre.trim()) { showToast('Nombre requerido', 'error'); return }
    setSaving(true)
    try {
      if (edit) {
        await updateMut.mutateAsync({ id: edit.id, ...form } as Record<string, unknown>)
        showToast('Servicio actualizado', 'success')
      } else {
        const created = await addMut.mutateAsync(form as unknown as Record<string, unknown>) as Servicio | undefined
        if (created?.id) setSelectedId(created.id)
        showToast('Servicio creado', 'success')
      }
      setModal(false)
    } catch { showToast('Error al guardar', 'error') } finally { setSaving(false) }
  }

  const doDelete = async () => {
    if (!del) return
    try {
      await removeMut.mutateAsync(del.id)
      showToast('Eliminado', 'success')
      if (selectedId === del.id) setSelectedId(null)
      setDel(null)
    } catch { showToast('Error', 'error') }
  }

  /* ─── Recursos ─── */
  const openNewRecurso = () => { setRecEdit(null); setRecMode('enlace'); setRecForm({ tipo: 'enlace', titulo: '', descripcion: '', url: '', publico: true, alcance: 'servicio' }); if (fileRef.current) fileRef.current.value = ''; setRecModal(true) }
  const openEditRecurso = (r: ServicioRecurso) => { setRecEdit(r); setRecMode('enlace'); setRecForm({ tipo: r.tipo, titulo: r.titulo, descripcion: r.descripcion ?? '', url: r.url, publico: r.publico, alcance: r.categoria ? 'linea' : 'servicio' }); setRecModal(true) }

  const saveRecurso = async () => {
    if (!selected) return
    if (!recForm.titulo.trim()) { showToast('Título requerido', 'error'); return }
    setRecSaving(true)
    try {
      if (recEdit) {
        await api.put(`/api/servicios/recursos/${recEdit.id}`, recForm)
      } else if (recMode === 'archivo') {
        const file = fileRef.current?.files?.[0]
        if (!file) { showToast('Elegí un archivo', 'error'); setRecSaving(false); return }
        const fd = new FormData()
        fd.append('file', file)
        fd.append('titulo', recForm.titulo.trim())
        fd.append('tipo', recForm.tipo)
        fd.append('descripcion', recForm.descripcion)
        fd.append('publico', String(recForm.publico))
        fd.append('alcance', recForm.alcance)
        await api.upload(`/api/servicios/${selected.id}/recursos/upload`, fd)
      } else {
        if (!recForm.url.trim()) { showToast('URL requerida', 'error'); setRecSaving(false); return }
        await api.post(`/api/servicios/${selected.id}/recursos`, recForm)
      }
      await invalidate()
      showToast(recEdit ? 'Recurso actualizado' : 'Recurso agregado', 'success')
      setRecModal(false)
    } catch { showToast('Error al guardar recurso', 'error') } finally { setRecSaving(false) }
  }

  const doDeleteRecurso = async () => {
    if (!recDel) return
    try {
      await api.del(`/api/servicios/recursos/${recDel.id}`)
      await invalidate()
      showToast('Recurso eliminado', 'success')
      setRecDel(null)
    } catch { showToast('Error', 'error') }
  }

  const copyLink = (url: string) => {
    const full = url.startsWith('http') ? url : window.location.origin + url
    navigator.clipboard.writeText(full)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  /* ─── Loading ─── */
  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Servicios' }]} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter">
      {selected ? renderDetail() : renderList()}

      {renderServiceModal()}
      {renderSecModal()}
      {renderRecursoModal()}
      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Confirmar Eliminación">
        <p>¿Eliminar el servicio <strong>{del?.nombre}</strong>? Se borrarán también sus recursos.</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={doDelete}>Eliminar</button>
        </div>
      </Modal>
      <Modal isOpen={!!recDel} onClose={() => setRecDel(null)} title="Eliminar Recurso">
        <p>¿Eliminar el recurso <strong>{recDel?.titulo}</strong>?</p>
        {recDel?.categoria && (
          <p style={{ fontSize: '0.82rem', color: 'var(--warning)', lineHeight: 1.5 }}>
            Es material de línea: desaparece de <strong>todos</strong> los servicios de «{recDel.categoria}», no solo de este.
          </p>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setRecDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={doDeleteRecurso}>Eliminar</button>
        </div>
      </Modal>
    </div>
  )

  /* ═══════════════════ LIST (tabla) ═══════════════════ */
  function renderList() {
    const chipToggle = (arr: string[], set: (v: string[]) => void, v: string) =>
      set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])

    return (
      <>
        <PageHeader
          breadcrumbs={[{ label: 'Servicios' }]}
          meta={`${filtered.length} de ${items.length}`}
          actions={isAdmin && <button className="btn btn-primary" onClick={openNew}><IconPlus size={16} /> Nuevo Servicio</button>}
        />

        {/* Toolbar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          <div style={{ position: 'relative', maxWidth: 380 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>{Ico.search}</span>
            <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Buscar servicio..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {(allEtiquetas.length > 0 || allIndustrias.length > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {allEtiquetas.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Etiquetas</span>
                  {allEtiquetas.map(t => {
                    const on = fEtiquetas.includes(t)
                    return <button key={t} onClick={() => chipToggle(fEtiquetas, setFEtiquetas, t)} className="badge" style={{ cursor: 'pointer', background: on ? 'var(--primary)' : 'var(--bg-secondary)', color: on ? 'var(--text-on-primary)' : 'var(--text-secondary)', border: '1px solid var(--border)' }}>{t}</button>
                  })}
                </div>
              )}
              {allIndustrias.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Industria</span>
                  {allIndustrias.map(t => {
                    const on = fIndustrias.includes(t)
                    return <button key={t} onClick={() => chipToggle(fIndustrias, setFIndustrias, t)} className="badge" style={{ cursor: 'pointer', background: on ? 'var(--info)' : 'var(--bg-secondary)', color: on ? 'var(--on-info)' : 'var(--text-secondary)', border: '1px solid var(--border)' }}>{t}</button>
                  })}
                </div>
              )}
              {(fEtiquetas.length > 0 || fIndustrias.length > 0 || search) && (
                <button className="btn btn-secondary btn-sm" onClick={() => { setFEtiquetas([]); setFIndustrias([]); setSearch('') }}>Limpiar</button>
              )}
            </div>
          )}
        </div>

        {/* Tabla */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-primary)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                <th style={thStyle}>Servicio</th>
                <th style={thStyle}>Etiquetas</th>
                <th style={thStyle}>Industria</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Recursos</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Precio</th>
                {isAdmin && <th style={{ ...thStyle, textAlign: 'right' }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={isAdmin ? 6 : 5} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Sin servicios que coincidan.</td></tr>
              ) : filtered.map(s => {
                const et = Array.isArray(s.etiquetas) ? s.etiquetas : []
                const ind = Array.isArray(s.industrias) ? s.industrias : []
                const recCount = Array.isArray(s.recursos) ? s.recursos.length : 0
                return (
                  <tr key={s.id} onClick={() => setSelectedId(s.id)} className="svc-row" style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{s.nombre}</div>
                      {s.descripcion && <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.descripcion}</div>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {et.slice(0, 3).map(t => <span key={t} className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)', fontSize: '0.68rem' }}>{t}</span>)}
                        {et.length > 3 && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>+{et.length - 3}</span>}
                        {et.length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {ind.map(t => <span key={t} className="badge" style={{ background: 'color-mix(in srgb, var(--info) 10%, transparent)', color: 'var(--info)', border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)', fontSize: '0.68rem' }}>{t}</span>)}
                        {ind.length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {recCount > 0
                        ? <span className="badge" style={{ background: 'color-mix(in srgb, var(--primary-light) 10%, transparent)', color: 'var(--primary)' }}>{recCount}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>0</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                      {s.precio_base > 0 ? formatCurrency(s.precio_base, s.moneda) : <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}
                    </td>
                    {isAdmin && (
                      <td style={{ ...tdStyle, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}><IconEdit size={14} /></button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDel(s)}><IconTrash size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  /* ═══════════════════ DETAIL (hoja-documento) ═══════════════════ */
  function renderDetail() {
    if (!selected) return null
    const et = Array.isArray(selected.etiquetas) ? selected.etiquetas : []
    const ind = Array.isArray(selected.industrias) ? selected.industrias : []
    const recursos = Array.isArray(selected.recursos) ? selected.recursos : []
    // Los de línea cuelgan de la categoría: son los mismos para todos los servicios de esa línea.
    const recPropios = recursos.filter(r => !r.categoria)
    const recDeLinea = recursos.filter(r => r.categoria)
    const incluye = Array.isArray(selected.incluye) ? selected.incluye : []
    const noIncluye = Array.isArray(selected.no_incluye) ? selected.no_incluye : []

    return (
      <>
        <PageHeader
          breadcrumbs={[
            { label: 'Servicios', onClick: () => setSelectedId(null) },
            { label: selected.nombre },
          ]}
          actions={isAdmin && (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => openEdit(selected)}><IconEdit size={14} /> Editar</button>
              <button className="btn btn-danger btn-sm" onClick={() => setDel(selected)}><IconTrash size={14} /></button>
            </>
          )}
        />

        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            {selected.precio_base > 0 && (
              <span className="badge" style={{ background: 'color-mix(in srgb, var(--primary-light) 10%, transparent)', color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(selected.precio_base, selected.moneda)} {selected.moneda}</span>
            )}
            {ind.map(t => <span key={t} className="badge" style={{ background: 'color-mix(in srgb, var(--info) 10%, transparent)', color: 'var(--info)', border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)' }}>{t}</span>)}
            {et.map(t => <span key={t} className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{t}</span>)}
          </div>
          {selected.descripcion && <p style={{ color: 'var(--text-secondary)', marginTop: 12, maxWidth: 820, lineHeight: 1.6 }}>{selected.descripcion}</p>}
        </div>

        {/* Documento */}
        <section style={{ marginBottom: 28 }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', fontWeight: 700, marginBottom: 12 }}><IconFileText size={16} /> Documento del servicio</h3>
          {selected.documento && selected.documento.trim()
            ? <div className="svc-doc" dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.documento) }} />
            : (
              <div style={{ padding: '28px 24px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-muted)' }}>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>Sin documento aún. {isAdmin && 'Usá “Editar” → pestaña Documento para redactar el proceso, pasos y T&C en Markdown.'}</p>
              </div>
            )}
        </section>

        {/* Incluye / No incluye */}
        {(incluye.length > 0 || noIncluye.length > 0) && (
          <section style={{ marginBottom: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, maxWidth: 820 }}>
            <div style={{ border: '1px solid color-mix(in srgb, var(--success) 20%, transparent)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: 'color-mix(in srgb, var(--success) 8%, transparent)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.82rem', color: 'var(--success)' }}><IconCheckCircle size={14} /> Qué Incluye</div>
              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                {incluye.length ? incluye.map((it, i) => <div key={i} style={{ display: 'flex', gap: 7, fontSize: '0.82rem', lineHeight: 1.5 }}><IconCheckCircle size={12} style={{ color: 'var(--success)', flexShrink: 0, marginTop: 3 }} /><span>{it}</span></div>) : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>No definido</span>}
              </div>
            </div>
            <div style={{ border: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: 'color-mix(in srgb, var(--danger) 6%, transparent)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.82rem', color: 'var(--danger)' }}><IconXCircle size={14} /> Qué NO Incluye</div>
              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                {noIncluye.length ? noIncluye.map((it, i) => <div key={i} style={{ display: 'flex', gap: 7, fontSize: '0.82rem', lineHeight: 1.5 }}><IconXCircle size={12} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 3 }} /><span>{it}</span></div>) : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>No definido</span>}
              </div>
            </div>
          </section>
        )}

        {/* Recursos */}
        <section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>{Ico.layers} Recursos <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.8rem' }}>({recursos.length})</span></h3>
            {isAdmin && <button className="btn btn-primary btn-sm" onClick={openNewRecurso}><IconPlus size={14} /> Agregar recurso</button>}
          </div>
          {recursos.length === 0 ? (
            <div style={{ padding: '28px 24px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-muted)', fontSize: '0.85rem' }}>Sin recursos aún.</div>
          ) : (
            /* Si no hay material de línea el subtítulo no dice nada: se muestra la grilla sola. */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {(recDeLinea.length === 0
                ? [['', recursos] as const]
                : [['De este servicio', recPropios] as const, [`De la línea — ${selected.categoria}`, recDeLinea] as const]
              ).filter(([, arr]) => arr.length > 0).map(([grupo, arr]) => (
              <div key={grupo}>
              {grupo && (
                <div style={{ fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 8 }}>
                  {grupo} <span style={{ fontWeight: 400 }}>({arr.length})</span>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
              {arr.map(r => {
                const m = TIPO_META[r.tipo] || TIPO_META.documento
                return (
                  <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span className="badge" style={{ background: m.bg, color: m.color, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.64rem', fontWeight: 700 }}>{m.label}</span>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '2px 7px' }} onClick={() => openEditRecurso(r)}><IconEdit size={12} /></button>
                          <button className="btn btn-danger btn-sm" style={{ padding: '2px 7px' }} onClick={() => setRecDel(r)}><IconX size={12} /></button>
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r.titulo}</div>
                      {r.descripcion && <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', lineHeight: 1.5, margin: '4px 0 0' }}>{r.descripcion}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>{Ico.ext} Abrir</a>
                      {r.publico && (
                        <button className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: copiedUrl === r.url ? 'var(--success)' : undefined }} onClick={() => copyLink(r.url)}>
                          {copiedUrl === r.url ? <>{Ico.check} Copiado</> : <>{Ico.link} Copiar link</>}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
              </div>
              </div>
              ))}
            </div>
          )}
        </section>
      </>
    )
  }

  /* ═══════════════════ Modales ═══════════════════ */
  function renderServiceModal() {
    return (
      <Modal isOpen={modal} onClose={() => setModal(false)} title={edit ? 'Editar Servicio' : 'Nuevo Servicio'}>
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          {(['general', 'documento', 'scope', 'secciones'] as const).map(tab => (
            <button key={tab} type="button" onClick={() => setActiveTab(tab)}
              style={{ padding: '8px 14px', background: 'none', border: 'none', borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent', color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: activeTab === tab ? 600 : 400, fontSize: '0.85rem', cursor: 'pointer' }}>
              {tab === 'general' ? 'General' : tab === 'documento' ? 'Documento' : tab === 'scope' ? 'Alcance' : 'Secciones'}
              {tab === 'scope' && (form.incluye.length + form.no_incluye.length > 0) && <span style={{ marginLeft: 4, fontSize: '0.7rem', opacity: 0.7 }}>({form.incluye.length + form.no_incluye.length})</span>}
              {tab === 'secciones' && form.secciones.length > 0 && <span style={{ marginLeft: 4, fontSize: '0.7rem', opacity: 0.7 }}>({form.secciones.length})</span>}
            </button>
          ))}
        </div>

        <form onSubmit={submit}>
          {activeTab === 'general' && (
            <>
              <div className="form-group"><label className="form-label">Nombre *</label><input className="form-input" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required autoFocus /></div>
              <div className="form-group"><label className="form-label">Resumen corto</label><textarea className="form-textarea" value={form.descripcion ?? ''} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} rows={2} placeholder="Una línea que resume el servicio (se muestra en la tabla)." /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Precio Base</label><input className="form-input" type="number" step="0.01" min="0" value={form.precio_base} onChange={e => setForm(f => ({ ...f, precio_base: parseFloat(e.target.value) || 0 }))} /></div>
                <div className="form-group"><label className="form-label">Moneda</label><select className="form-select" value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value as Moneda }))}><option value="USD">USD</option><option value="PEN">PEN</option><option value="EUR">EUR</option></select></div>
              </div>
              <div className="form-group">
                <label className="form-label">Etiquetas</label>
                <div style={{ display: 'flex', gap: 8 }}><input className="form-input" value={tagIn} onChange={e => setTagIn(e.target.value)} placeholder="Ej: tecnología, marketing" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} /><button type="button" className="btn btn-secondary btn-sm" onClick={addTag}>+</button></div>
                {form.etiquetas.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>{form.etiquetas.map(t => <span key={t} className="badge" style={{ cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }} onClick={() => rmTag(t)}>{t} ×</span>)}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Industria / Nicho</label>
                <div style={{ display: 'flex', gap: 8 }}><input className="form-input" value={indIn} onChange={e => setIndIn(e.target.value)} placeholder="Ej: inmobiliaria, retail" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addInd())} /><button type="button" className="btn btn-secondary btn-sm" onClick={addInd}>+</button></div>
                {form.industrias.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>{form.industrias.map(t => <span key={t} className="badge" style={{ cursor: 'pointer', background: 'color-mix(in srgb, var(--info) 10%, transparent)', color: 'var(--info)', border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)' }} onClick={() => rmInd(t)}>{t} ×</span>)}</div>}
              </div>
              <div className="form-group">
                <label className="form-label" style={{ color: 'var(--text-muted)' }}>Categoría <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>(solo para cotizaciones)</span></label>
                <select className="form-select" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value as CategoriaServicio }))}>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </>
          )}

          {activeTab === 'documento' && (
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>Documento (Markdown)</label>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDocPreview(p => !p)}>{docPreview ? 'Editar' : 'Vista previa'}</button>
              </div>
              {docPreview
                ? <div className="svc-doc" style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, minHeight: 340, maxHeight: 460, overflowY: 'auto' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(form.documento || '') }} />
                : <textarea className="form-textarea svc-md-editor" value={form.documento ?? ''} onChange={e => setForm(f => ({ ...f, documento: e.target.value }))} placeholder={'# Título\n\n## Proceso\n\n1. Paso uno\n2. Paso dos\n\n| Etapa | Entregable |\n|---|---|\n| ... | ... |\n\n## Términos y Condiciones\n...'} />}
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>Soporta encabezados, listas, <strong>tablas</strong>, citas y código. Los T&C van como una sección dentro del documento.</p>
            </div>
          )}

          {activeTab === 'scope' && (
            <>
              <div className="form-group">
                <label className="form-label" style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 5 }}><IconCheckCircle size={14} /> Qué Incluye ({form.incluye.length})</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" value={incluyeIn} onChange={e => setIncluyeIn(e.target.value)} placeholder="Ej: Diagnóstico operativo, capacitación..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addIncluye())} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addIncluye}>+</button>
                </div>
                {form.incluye.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                    {form.incluye.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'color-mix(in srgb, var(--success) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--success) 15%, transparent)', borderRadius: 6 }}>
                        <IconCheckCircle size={13} style={{ color: 'var(--success)', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '0.85rem' }}>{item}</span>
                        <button type="button" onClick={() => rmIncluye(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><IconX size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label" style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 5 }}><IconXCircle size={14} /> Qué NO Incluye ({form.no_incluye.length})</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-input" value={noIncluyeIn} onChange={e => setNoIncluyeIn(e.target.value)} placeholder="Ej: Licencias de terceros, hosting..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNoIncluye())} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addNoIncluye}>+</button>
                </div>
                {form.no_incluye.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                    {form.no_incluye.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'color-mix(in srgb, var(--danger) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 15%, transparent)', borderRadius: 6 }}>
                        <IconXCircle size={13} style={{ color: 'var(--danger)', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '0.85rem' }}>{item}</span>
                        <button type="button" onClick={() => rmNoIncluye(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><IconX size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'secciones' && (
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>Secciones de Propuesta ({form.secciones.length})</label>
                <button type="button" className="btn btn-primary btn-sm" onClick={openNewSeccion}><IconPlus size={14} /> Nueva</button>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>Estas secciones se copian a la cotización al usar el servicio (distintas del Documento).</p>
              {form.secciones.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '8px 0', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 8 }}>Sin secciones.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {form.secciones.map((sec, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-secondary)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{sec.titulo}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sec.contenido ? (sec.contenido.replace(/<[^>]+>/g, '').slice(0, 80) || 'Vacío') : 'Vacío'}</div>
                      </div>
                      <button type="button" className="btn btn-sm btn-secondary" disabled={i === 0} onClick={() => moveSeccion(i, -1)} style={{ padding: '2px 6px', opacity: i === 0 ? 0.3 : 1 }}>▲</button>
                      <button type="button" className="btn btn-sm btn-secondary" disabled={i === form.secciones.length - 1} onClick={() => moveSeccion(i, 1)} style={{ padding: '2px 6px', opacity: i === form.secciones.length - 1 ? 0.3 : 1 }}>▼</button>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => openEditSeccion(i)}>Editar</button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => removeSeccion(i)}><IconX size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : edit ? 'Actualizar' : 'Crear'}</button>
          </div>
        </form>
      </Modal>
    )
  }

  function renderSecModal() {
    return (
      <Modal isOpen={showSecModal} onClose={closeSecModal} title={editSecIdx !== null ? 'Editar Sección' : 'Nueva Sección'}>
        <div className="form-group"><label className="form-label">Título</label><input className="form-input" value={secTitulo} onChange={e => setSecTitulo(e.target.value)} placeholder="Ej: Resumen Ejecutivo" autoFocus /></div>
        <div className="form-group"><label className="form-label">Contenido</label><RichTextEditor content={secContenido} onChange={setSecContenido} placeholder="Contenido de la sección..." minHeight={300} /></div>
        <div className="modal-actions" style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-secondary" onClick={closeSecModal}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={saveSeccion}>Guardar Sección</button>
        </div>
      </Modal>
    )
  }

  function renderRecursoModal() {
    return (
      <Modal isOpen={recModal} onClose={() => setRecModal(false)} title={recEdit ? 'Editar Recurso' : 'Agregar Recurso'}>
        {!recEdit && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {(['enlace', 'archivo'] as const).map(m => (
              <button key={m} type="button" onClick={() => setRecMode(m)} className={`btn btn-sm ${recMode === m ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}>
                {m === 'enlace' ? 'Enlace externo' : 'Subir archivo'}
              </button>
            ))}
          </div>
        )}

        {recEdit?.categoria && (
          <p style={{ fontSize: '0.78rem', color: 'var(--warning)', lineHeight: 1.5, margin: '0 0 12px' }}>
            Material de línea: los cambios se ven en todos los servicios de «{recEdit.categoria}».
          </p>
        )}

        {!recEdit && selected && (
          <div className="form-group">
            <label className="form-label">Alcance</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {([['servicio', 'Este servicio'], ['linea', 'Toda la línea']] as const).map(([v, label]) => (
                <button key={v} type="button" onClick={() => setRecForm(f => ({ ...f, alcance: v }))}
                  className={`btn btn-sm ${recForm.alcance === v ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }}>
                  {label}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {recForm.alcance === 'linea'
                ? `Aparece en todos los servicios de «${selected.categoria}». Es un solo archivo: editarlo los actualiza a todos.`
                : `Solo en ${selected.nombre}.`}
            </p>
          </div>
        )}

        <div className="form-group"><label className="form-label">Título *</label><input className="form-input" value={recForm.titulo} onChange={e => setRecForm(f => ({ ...f, titulo: e.target.value }))} autoFocus /></div>
        <div className="form-group"><label className="form-label">Descripción</label><textarea className="form-textarea" rows={2} value={recForm.descripcion} onChange={e => setRecForm(f => ({ ...f, descripcion: e.target.value }))} /></div>
        <div className="form-group">
          <label className="form-label">Tipo</label>
          <select className="form-select" value={recForm.tipo} onChange={e => setRecForm(f => ({ ...f, tipo: e.target.value as TipoRecurso }))}>
            {TIPOS_RECURSO.map(t => <option key={t} value={t}>{TIPO_META[t].label}</option>)}
          </select>
        </div>

        {recEdit || recMode === 'enlace' ? (
          <div className="form-group"><label className="form-label">URL *</label><input className="form-input" value={recForm.url} onChange={e => setRecForm(f => ({ ...f, url: e.target.value }))} placeholder="https://... o /presentaciones/archivo.html" /></div>
        ) : (
          <div className="form-group"><label className="form-label">Archivo *</label><input ref={fileRef} type="file" className="form-input" /><p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>PDF, PPT/PPTX, DOC/DOCX, HTML, imágenes, video. Máx 50 MB.</p></div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer', marginTop: 4 }}>
          <input type="checkbox" checked={recForm.publico} onChange={e => setRecForm(f => ({ ...f, publico: e.target.checked }))} />
          Compartible por link público
        </label>

        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={() => setRecModal(false)}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={saveRecurso} disabled={recSaving}>{recSaving ? 'Guardando...' : recEdit ? 'Actualizar' : recMode === 'archivo' ? 'Subir' : 'Agregar'}</button>
        </div>
      </Modal>
    )
  }
}

const thStyle: CSSProperties = { padding: '11px 16px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)' }
const tdStyle: CSSProperties = { padding: '11px 16px', verticalAlign: 'middle' }
